import { assetPlaybackBlockers, } from "./channel-playability";
export const VOD_ASSET_TYPES = [
    "movie",
    "series",
    "season",
    "episode",
];
export const PARENT_FIELDS_BY_TYPE = {
    movie: [],
    series: [],
    season: ["seriesId", "seasonNumber"],
    episode: ["seriesId", "seasonId", "seasonNumber", "episodeNumber"],
};
export function isContainerType(type) {
    return type === "series" || type === "season";
}
export function isVodAssetType(value) {
    return (typeof value === "string" &&
        VOD_ASSET_TYPES.includes(value));
}
export const VOD_PLAYABLE_MEDIA_TYPES = ["main"];
export const ASSET_FIELD_LIMITS = {
    externalId: 255,
    originalTitle: 255,
    originalLanguage: 3,
    countryOfOrigin: 3,
    copyrightInfo: 255,
    studio: 255,
    eidrId: 255,
    seriesId: 64,
    seasonId: 64,
    keyword: 64,
    language: 3,
    country: 3,
};
export function canonicalAssetLanguage(value) {
    if (typeof value !== "string")
        return null;
    const code = value.trim().toLowerCase();
    return /^[a-z]{2,3}$/.test(code) ? code : null;
}
export function narrowAssetLanguage(value) {
    if (typeof value !== "string")
        return null;
    const raw = value.trim();
    if (raw === "")
        return null;
    const direct = canonicalAssetLanguage(raw);
    if (direct)
        return { code: direct };
    const primary = raw.split(/[-_]/)[0];
    const code = canonicalAssetLanguage(primary);
    return code ? { code, narrowedFrom: raw } : null;
}
function integerFrom(value) {
    if (typeof value === "number") {
        return Number.isFinite(value) ? Math.round(value) : null;
    }
    if (typeof value !== "string")
        return null;
    const trimmed = value.trim();
    if (trimmed === "" || !/^-?\d+$/.test(trimmed))
        return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
}
export const CANONICAL_YEAR_FIELD = "productionYear";
export const ASSET_YEAR_MIN = 1888;
export const ASSET_YEAR_MAX = 2999;
export function parseAssetYear(value) {
    const n = integerFrom(value);
    if (n === null)
        return null;
    return n >= ASSET_YEAR_MIN && n <= ASSET_YEAR_MAX ? n : null;
}
export function titleYear(asset) {
    return typeof asset.productionYear === "number" &&
        Number.isFinite(asset.productionYear)
        ? asset.productionYear
        : null;
}
export function yearIssues(asset) {
    const production = titleYear(asset);
    const release = typeof asset.releaseYear === "number" && Number.isFinite(asset.releaseYear)
        ? asset.releaseYear
        : null;
    if (release === null)
        return [];
    if (production === null) {
        return [{ code: "year_only_in_release_year", releaseYear: release }];
    }
    if (production !== release) {
        return [
            {
                code: "years_disagree",
                productionYear: production,
                releaseYear: release,
            },
        ];
    }
    return [];
}
export const CANONICAL_DURATION_UNIT = "seconds";
export const ASSET_DURATION_MAX_SECONDS = 100 * 60 * 60;
export function parseRunningTimeSeconds(value) {
    if (typeof value !== "string")
        return null;
    const text = value.trim().toLowerCase();
    if (text === "")
        return null;
    const plain = /^(\d+)$/.exec(text);
    if (plain)
        return boundedSeconds(Number(plain[1]) * 60);
    const secondsOnly = /^(\d+)\s*(s|sec|secs|second|seconds)$/.exec(text);
    if (secondsOnly)
        return boundedSeconds(Number(secondsOnly[1]));
    const minutesOnly = /^(\d+)\s*(m|min|mins|minute|minutes)$/.exec(text);
    if (minutesOnly)
        return boundedSeconds(Number(minutesOnly[1]) * 60);
    const withHours = /^(\d+)\s*(h|hr|hrs|hour|hours)(?:\s*(\d+)\s*(?:m|min|mins|minute|minutes)?)?$/.exec(text);
    if (withHours) {
        const hours = Number(withHours[1]);
        const minutes = withHours[3] === undefined ? 0 : Number(withHours[3]);
        if (minutes > 59)
            return null;
        return boundedSeconds(hours * 3600 + minutes * 60);
    }
    return null;
}
function boundedSeconds(n) {
    if (!Number.isFinite(n))
        return null;
    return n > 0 && n <= ASSET_DURATION_MAX_SECONDS ? n : null;
}
export function assetDurationSeconds(asset) {
    return typeof asset.duration === "number" &&
        Number.isFinite(asset.duration) &&
        asset.duration > 0
        ? Math.round(asset.duration)
        : null;
}
export const DURATION_TOLERANCE_SECONDS = 5;
export function durationIssues(asset) {
    var _a;
    const main = ((_a = asset.medias) !== null && _a !== void 0 ? _a : []).find((media) => VOD_PLAYABLE_MEDIA_TYPES.includes(media.type));
    const millis = main === null || main === void 0 ? void 0 : main.durationMillis;
    if (typeof millis !== "number" || !Number.isFinite(millis) || millis <= 0) {
        return [];
    }
    const mediaSeconds = Math.round(millis / 1000);
    const assetSeconds = assetDurationSeconds(asset);
    if (assetSeconds === null) {
        return [{ code: "duration_missing", mediaSeconds }];
    }
    if (Math.abs(assetSeconds - mediaSeconds) <= DURATION_TOLERANCE_SECONDS) {
        return [];
    }
    if (Math.abs(assetSeconds * 60 - mediaSeconds) <=
        DURATION_TOLERANCE_SECONDS * 60) {
        return [{ code: "duration_may_be_minutes", assetSeconds, mediaSeconds }];
    }
    return [
        { code: "duration_disagrees_with_media", assetSeconds, mediaSeconds },
    ];
}
export const CANONICAL_GENRE_MODEL = "genre-entity";
export const GENRE_TAG_TYPE = "genre";
export function assetGenres(asset) {
    var _a;
    return [...((_a = asset.genres) !== null && _a !== void 0 ? _a : [])];
}
export function assetGenreIds(asset) {
    return assetGenres(asset).map((genre) => genre.genreId);
}
export function genreTags(asset) {
    var _a;
    return ((_a = asset.tags) !== null && _a !== void 0 ? _a : []).filter((tag) => tag.type === GENRE_TAG_TYPE);
}
export function genreDisplayName(genre, language) {
    var _a;
    const code = canonicalAssetLanguage(language);
    if (code) {
        const localised = ((_a = genre.localizedName) !== null && _a !== void 0 ? _a : []).find((entry) => canonicalAssetLanguage(entry.language) === code);
        if (localised && localised.name.trim() !== "")
            return localised.name;
    }
    return genre.name;
}
export function resolveGenreId(value, catalogue) {
    var _a, _b;
    if (typeof value !== "string")
        return null;
    const needle = value.trim().toLowerCase();
    if (needle === "")
        return null;
    for (const genre of catalogue) {
        if (genre.genreId.toLowerCase() === needle)
            return genre.genreId;
    }
    for (const genre of catalogue) {
        if (genre.name.trim().toLowerCase() === needle)
            return genre.genreId;
        const localised = ((_a = genre.localizedName) !== null && _a !== void 0 ? _a : []).some((entry) => entry.name.trim().toLowerCase() === needle);
        if (localised)
            return genre.genreId;
        const alternative = ((_b = genre.alternativeIds) !== null && _b !== void 0 ? _b : []).some((id) => id.trim().toLowerCase() === needle);
        if (alternative)
            return genre.genreId;
    }
    return null;
}
export function slugGenreId(name) {
    if (typeof name !== "string")
        return null;
    const slug = name
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    if (slug === "")
        return null;
    return slug.slice(0, ASSET_FIELD_LIMITS.seriesId);
}
export function genreDeliveryGaps(asset) {
    const genres = assetGenres(asset);
    const tags = genreTags(asset);
    const tagNames = new Set(tags.map((tag) => tag.name.trim().toLowerCase()));
    const genreNames = new Set(genres.map((genre) => genre.name.trim().toLowerCase()));
    return {
        genresNotDelivered: genres
            .filter((genre) => !tagNames.has(genre.name.trim().toLowerCase()))
            .map((genre) => genre.genreId),
        tagsWithoutGenre: tags
            .filter((tag) => !genreNames.has(tag.name.trim().toLowerCase()))
            .map((tag) => tag.tagId),
    };
}
export function normaliseKeyword(value) {
    if (typeof value !== "string")
        return null;
    const text = value.trim().replace(/\s+/g, " ");
    if (text === "")
        return null;
    return text.length <= ASSET_FIELD_LIMITS.keyword ? text : null;
}
export function normaliseKeywords(values) {
    if (!Array.isArray(values))
        return [];
    const seen = new Set();
    const out = [];
    for (const value of values) {
        const keyword = normaliseKeyword(value);
        if (!keyword)
            continue;
        const key = keyword.toLowerCase();
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(keyword);
    }
    return out;
}
export function buildSeriesTree(series, rows) {
    var _a, _b;
    const children = rows.filter((row) => row.assetId !== series.assetId);
    const seasonRows = children.filter((row) => row.type === "season" && row.seriesId === series.assetId);
    const byAssetId = new Map(seasonRows.map((row) => [row.assetId, row]));
    const episodeRows = children.filter((row) => row.type === "episode" &&
        (row.seriesId === series.assetId ||
            (!!row.seasonId && byAssetId.has(row.seasonId))));
    const nodes = new Map();
    const keyOf = (season, seasonNumber) => season ? `id:${season.assetId}` : `num:${seasonNumber !== null && seasonNumber !== void 0 ? seasonNumber : "none"}`;
    const byNumber = new Map();
    for (const season of seasonRows) {
        const seasonNumber = numberOrNull(season.seasonNumber);
        nodes.set(keyOf(season, seasonNumber), {
            season,
            seasonNumber,
            episodes: [],
        });
        if (seasonNumber !== null && !byNumber.has(seasonNumber)) {
            byNumber.set(seasonNumber, season);
        }
    }
    const looseEpisodes = [];
    for (const episode of episodeRows) {
        const seasonNumber = numberOrNull(episode.seasonNumber);
        const parent = (_a = (episode.seasonId ? byAssetId.get(episode.seasonId) : undefined)) !== null && _a !== void 0 ? _a : (seasonNumber !== null ? byNumber.get(seasonNumber) : undefined);
        if (!parent && seasonNumber === null) {
            looseEpisodes.push(episode);
            continue;
        }
        const key = keyOf(parent !== null && parent !== void 0 ? parent : null, (_b = numberOrNull(parent === null || parent === void 0 ? void 0 : parent.seasonNumber)) !== null && _b !== void 0 ? _b : seasonNumber);
        let node = nodes.get(key);
        if (!node) {
            node = { season: parent !== null && parent !== void 0 ? parent : null, seasonNumber, episodes: [] };
            nodes.set(key, node);
        }
        node.episodes.push(episode);
    }
    const seasons = [...nodes.values()].sort(compareSeasonNodes);
    for (const node of seasons) {
        node.episodes.sort(compareEpisodes);
    }
    return {
        series,
        seasons,
        looseEpisodes: looseEpisodes.sort(compareEpisodes),
    };
}
function numberOrNull(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}
export function compareSeasonNodes(a, b) {
    if (a.seasonNumber === b.seasonNumber)
        return 0;
    if (a.seasonNumber === null)
        return 1;
    if (b.seasonNumber === null)
        return -1;
    return a.seasonNumber - b.seasonNumber;
}
export function compareEpisodes(a, b) {
    const left = numberOrNull(a.episodeNumber);
    const right = numberOrNull(b.episodeNumber);
    if (left === right)
        return a.assetId < b.assetId ? -1 : 1;
    if (left === null)
        return 1;
    if (right === null)
        return -1;
    return left - right;
}
export function seriesCounts(tree, complete = true) {
    return {
        seasons: tree.seasons.length,
        episodes: tree.seasons.reduce((sum, node) => sum + node.episodes.length, 0),
        looseEpisodes: tree.looseEpisodes.length,
        complete,
    };
}
export function titlePlaybackBlockers(asset) {
    return assetPlaybackBlockers(asset, VOD_PLAYABLE_MEDIA_TYPES);
}
export function isTitlePlayable(asset) {
    return titlePlaybackBlockers(asset).length === 0;
}
export function seriesPlaybackBlockers(tree) {
    if (tree.seasons.length === 0 && tree.looseEpisodes.length === 0) {
        return [{ code: "series_has_no_seasons" }];
    }
    const episodes = [
        ...tree.seasons.flatMap((node) => node.episodes),
        ...tree.looseEpisodes,
    ];
    const anyPlayable = episodes.some((episode) => isTitlePlayable(episode));
    return anyPlayable ? [] : [{ code: "series_has_no_playable_episode" }];
}
export function unplayableEpisodes(tree) {
    const out = [];
    for (const node of tree.seasons) {
        for (const episode of node.episodes) {
            const blockers = titlePlaybackBlockers(episode);
            if (blockers.length > 0) {
                out.push({ episode, seasonNumber: node.seasonNumber, blockers });
            }
        }
    }
    for (const episode of tree.looseEpisodes) {
        const blockers = titlePlaybackBlockers(episode);
        if (blockers.length > 0) {
            out.push({ episode, seasonNumber: null, blockers });
        }
    }
    return out;
}
export const AVAILABILITY_MODEL = "contentRights";
export const ASSET_REFERENCE_PREFIX = "NENDA-";
export const ASSET_REFERENCE_START = 100241;
export function formatAssetReference(sequence) {
    return `${ASSET_REFERENCE_PREFIX}${sequence}`;
}
export function canonicalAssetReference(value) {
    if (typeof value !== "string")
        return null;
    const text = value.trim().toUpperCase();
    return /^NENDA-\d{4,12}$/.test(text) ? text : null;
}
export function isAssetReference(value) {
    return canonicalAssetReference(value) !== null;
}
